"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface NotificationPopoverProps {
  visible: boolean;
  message: string;
  title?: string;
  onDismiss?: () => void;
  actionButton?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
  autoDismissMs?: number | null;
  position?: "center" | "card";
  cardElement?: Element | null;
  listingId?: string;
}

export function NotificationPopover({
  visible,
  message,
  title,
  onDismiss,
  actionButton,
  duration,
  autoDismissMs,
  position = "center",
  cardElement,
  listingId,
}: NotificationPopoverProps) {
  const containerRef = useRef<HTMLElement | null>(null);
  const rich = Boolean(title || onDismiss || actionButton);
  const resolvedAutoMs =
    autoDismissMs !== undefined ? autoDismissMs : duration !== undefined ? duration : undefined;

  useEffect(() => {
    if (!containerRef.current) {
      const host = document.createElement("div");
      host.setAttribute("data-notification-popover", "true");
      document.body.appendChild(host);
      containerRef.current = host;
    }
    return () => {
      if (containerRef.current && document.body.contains(containerRef.current)) {
        document.body.removeChild(containerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!visible || !onDismiss) return;
    let ms: number | null;
    if (resolvedAutoMs !== undefined) {
      ms = resolvedAutoMs === null ? null : resolvedAutoMs;
    } else if (actionButton) {
      ms = null;
    } else {
      ms = 2200;
    }
    if (ms == null) return;
    const t = window.setTimeout(() => onDismiss(), ms);
    return () => clearTimeout(t);
  }, [visible, onDismiss, actionButton, resolvedAutoMs]);

  const [anchorStyle, setAnchorStyle] = useState<React.CSSProperties | null>(null);

  useEffect(() => {
    if (!visible || position !== "card") {
      setAnchorStyle(null);
      return;
    }

    const updatePosition = () => {
      if (cardElement) {
        const rect = cardElement.getBoundingClientRect();
        setAnchorStyle({
          left: rect.left + rect.width / 2,
          top: rect.top + rect.height / 2,
        });
        return;
      }
      if (listingId) {
        const button = document.querySelector(`[data-listing-id="${listingId}"]`);
        if (button) {
          let card: Element | null = null;
          let current: Element | null = button.parentElement;
          let depth = 0;
          while (current && depth < 15) {
            const classes = String(current.className || "");
            const rect = current.getBoundingClientRect();

            if (classes.includes("relative") && classes.includes("h-full") && classes.includes("w-full") && rect.height > 400) {
              card = current;
              break;
            }

            if (!card && rect.height > 400 && rect.width > 200 && rect.height / rect.width > 1.2) {
              card = current;
            }

            current = current.parentElement;
            depth++;
          }

          if (card) {
            const rect = card.getBoundingClientRect();
            setAnchorStyle({
              left: rect.left + rect.width / 2,
              top: rect.top + rect.height / 2,
            });
            return;
          }
        }
      }
      setAnchorStyle(null);
    };

    updatePosition();
    const interval = window.setInterval(updatePosition, 120);
    return () => clearInterval(interval);
  }, [visible, position, cardElement, listingId]);

  if (!visible) return null;

  if (!containerRef.current) {
    const host = document.createElement("div");
    host.setAttribute("data-notification-popover", "true");
    document.body.appendChild(host);
    containerRef.current = host;
  }

  const panel = rich ? (
    <div
      className={cn(
        "pointer-events-auto relative w-[min(calc(100vw-2rem),22rem)] rounded-2xl border border-border/80 bg-card p-4 pt-5 text-card-foreground shadow-xl",
        "dark:border-white/12 dark:bg-zinc-950/95 dark:backdrop-blur-xl",
        "animate-in fade-in zoom-in-95 slide-in-from-bottom-3 duration-300",
      )}
    >
      {onDismiss ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1.5 top-1.5 h-8 w-8 rounded-full text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}
      {title ? <p className="pr-8 text-sm font-semibold leading-tight tracking-tight text-foreground">{title}</p> : null}
      <p
        className={cn(
          "text-sm leading-snug text-muted-foreground",
          title ? "mt-1.5" : "pr-8",
        )}
      >
        {message}
      </p>
      {actionButton ? (
        <Button
          type="button"
          variant="default"
          size="sm"
          className="mt-4 w-full rounded-full"
          onClick={actionButton.onClick}
        >
          {actionButton.label}
        </Button>
      ) : null}
    </div>
  ) : (
    <div
      className={cn(
        "pointer-events-auto max-w-[min(calc(100vw-2rem),20rem)] rounded-full border border-border/80 bg-card px-4 py-2.5 text-center text-sm font-medium text-foreground shadow-lg",
        "dark:border-white/12 dark:bg-zinc-950/95 dark:backdrop-blur-xl",
        "animate-in fade-in zoom-in-95 duration-200",
      )}
    >
      {message}
    </div>
  );

  const anchored =
    position === "card" && anchorStyle != null && typeof anchorStyle.left === "number" && typeof anchorStyle.top === "number";

  const content = anchored ? (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed z-[100000]"
      style={{
        left: anchorStyle!.left,
        top: anchorStyle!.top,
        transform: "translate(-50%, -50%)",
      }}
    >
      {panel}
    </div>
  ) : (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-[max(5.5rem,calc(env(safe-area-inset-bottom)+5rem))] z-[100000] flex justify-center px-4 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2"
    >
      {panel}
    </div>
  );

  return createPortal(content, containerRef.current);
}
